import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  List,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_ATTRIBUTES } from "@/services/XCONS_ENV";
import type { AttributeDetailDto, AttributeImpact } from "../types";
import {
  CampoDeValor,
  cuerpoDelValor,
  llevaColor,
  llevaImagen,
} from "../CampoDeValor";
import { Muestra } from "../SelectorDeOpcion";

const { Text, Paragraph } = Typography;

/** Los tipos que el ABM ofrece, con la tabla de valores que le corresponde a cada uno. */
const TIPOS: Array<{
  value: string;
  label: string;
  backendType: string;
}> = [
  { value: "text", label: "Texto", backendType: "varchar" },
  { value: "textarea", label: "Texto largo", backendType: "text" },
  { value: "select", label: "Lista (una opción)", backendType: "int" },
  {
    value: "multiselect",
    label: "Lista (varias opciones)",
    backendType: "varchar",
  },
  { value: "boolean", label: "Sí / No", backendType: "int" },
  { value: "price", label: "Precio", backendType: "decimal" },
  { value: "date", label: "Fecha", backendType: "datetime" },
];

/**
 * La config de catálogo de `catalog_eav_attribute`. Las etiquetas dicen **qué hace** cada
 * flag, no cómo se llama la columna: "buscable" y "filtrable" no significan nada solos.
 *
 * Son las cuatro que pidió el PM (design.md B2.2) y ninguna más (xcons-docs#408).
 */
const CONFIG: Array<{ key: string; label: string; hint: string }> = [
  {
    key: "isFilterable",
    label: "Filtrable en el catálogo",
    hint: "Alimenta las facetas del listado de productos",
  },
  {
    key: "isSearchable",
    label: "Buscable",
    hint: "El texto del valor entra en la búsqueda",
  },
  {
    key: "usedInProductListing",
    label: "En el listado",
    hint: "Viaja en el listado, no sólo en la ficha",
  },
  {
    key: "isComparable",
    label: "Comparable",
    hint: "Aparece en el comparador",
  },
];

/**
 * Los flags que el form ya no muestra pero siguen existiendo en `catalog_eav_attribute`.
 * Al editar viajan con el valor que ya tenían, para que sacarlos del form no los apague;
 * al crear, con el default de Magento (visible, sin faceta en búsqueda).
 */
const CONFIG_OCULTA: Record<string, number> = {
  isFilterableInSearch: 0,
  isVisible: 1,
};

/**
 * El cuerpo del panel con scroll y el pie fijo abajo, como el de los Filtros. Es un
 * componente y no un `div` porque `LateralModal` le pasa `closeModal` a su hijo, y un
 * `div` no lo acepta.
 */
const CuerpoConPie: React.FC<{
  children: React.ReactNode;
  pie: React.ReactNode;
  closeModal?: () => void;
}> = ({ children, pie }) => (
  <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      {children}
    </div>
    <Flex
      justify="space-between"
      gap={8}
      style={{
        flexShrink: 0,
        padding: "12px 24px",
        borderTop: "1px solid #f0f0f0",
        backgroundColor: "#fff",
      }}
    >
      {pie}
    </Flex>
  </Flex>
);

const esOpcion = (input?: string | null) =>
  input === "select" || input === "multiselect";

/**
 * Crear o editar un atributo (PIM Fase 16).
 *
 * **El aviso de impacto se pide al abrir en modo edición, no al guardar.** Si apareciera al
 * apretar Guardar ya sería tarde: la idea es que nadie escriba nada sin saber que lo que
 * está por tocar lo usan 27.211 productos.
 *
 * **No hay borrado de nada** —ni del atributo, ni de sus opciones—: es la excepción declarada
 * a R14. Borrar un producto afecta a uno; borrar un atributo destruye valores en todos. Una
 * opción se **renombra**, y los productos siguen apuntando al mismo `option_id`.
 */
export const AttributeDrawer: React.FC<{
  /** `null` = crear. */
  code: string | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}> = ({ code, open, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const esNuevo = code === null;

  const [detalle, setDetalle] = useState<AttributeDetailDto | null>(null);
  const [impacto, setImpacto] = useState<AttributeImpact | null>(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [nuevaOpcion, setNuevaOpcion] = useState("");
  /** La imagen del valor nuevo, en los atributos que la llevan (xcons-docs#490). */
  const [imagenNueva, setImagenNueva] = useState<File | null>(null);
  const [colorNuevo, setColorNuevo] = useState<string | null>(null);
  /**
   * La muestra de cada valor creado acá, por nombre: mientras la API no la devuelva, el valor
   * recién agregado se ve igual con su color o su imagen (xcons-docs#499).
   */
  const [muestrasCreadas, setMuestrasCreadas] = useState<Record<string, string>>({});
  const muestraDe = (o: AttributeDetailDto["options"][number]) =>
    o.image || muestrasCreadas[(o.label ?? "").toLowerCase()];
  const [renombrando, setRenombrando] = useState<{
    optionId: number;
    label: string;
  } | null>(null);

  const cargar = useCallback(async () => {
    if (!code) {
      form.resetFields();
      setDetalle(null);
      setImpacto(null);
      return;
    }
    setLoading(true);
    try {
      const [d, i] = await Promise.all([
        axiosInstance.get<AttributeDetailDto>(
          `${API_URL_PIM_ATTRIBUTES}/${encodeURIComponent(code)}`,
        ),
        axiosInstance.get<AttributeImpact>(
          `${API_URL_PIM_ATTRIBUTES}/${encodeURIComponent(code)}/impact`,
        ),
      ]);
      setDetalle(d.data);
      setImpacto(i.data);
      form.setFieldsValue({
        code: d.data.code,
        label: d.data.label,
        frontendInput: d.data.frontendInput,
        position: Number(d.data.catalog.position ?? 0),
        ...Object.fromEntries(
          CONFIG.map((c) => [c.key, Number(d.data.catalog[c.key] ?? 0) === 1]),
        ),
      });
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo cargar el atributo",
      );
    } finally {
      setLoading(false);
    }
  }, [code, form]);

  useEffect(() => {
    if (open) void cargar();
  }, [open, cargar]);

  const guardar = async () => {
    const values = await form.validateFields();
    const catalog = {
      ...Object.fromEntries(
        Object.entries(CONFIG_OCULTA).map(([key, porDefecto]) => [
          key,
          esNuevo ? porDefecto : Number(detalle?.catalog[key] ?? porDefecto),
        ]),
      ),
      ...Object.fromEntries(CONFIG.map((c) => [c.key, values[c.key] ? 1 : 0])),
      // El orden (B2): la posición del atributo entre las facetas del catálogo.
      ...(values.position !== undefined && values.position !== null
        ? { position: Number(values.position) }
        : {}),
    };

    setGuardando(true);
    try {
      if (esNuevo) {
        const tipo = TIPOS.find((t) => t.value === values.frontendInput);
        await axiosInstance.post(API_URL_PIM_ATTRIBUTES, {
          code: values.code,
          label: values.label,
          frontendInput: values.frontendInput,
          // El `backend_type` sale del tipo elegido y NO se ofrece: es la tabla donde viven
          // los valores, y elegirlo mal no se puede deshacer después.
          backendType: tipo?.backendType ?? "varchar",
          catalog,
        });
        message.success("Atributo creado");
      } else {
        await axiosInstance.patch(
          `${API_URL_PIM_ATTRIBUTES}/${encodeURIComponent(code!)}`,
          {
            label: values.label,
            // El tipo sólo viaja si cambió: mandarlo igual haría que el backend evalúe el
            // bloqueo en cada guardado y rechace una edición de etiqueta.
            ...(values.frontendInput !== detalle?.frontendInput
              ? { frontendInput: values.frontendInput }
              : {}),
            catalog,
          },
        );
        message.success("Atributo actualizado");
      }
      onSaved();
      onClose();
    } catch (e) {
      const res = (
        e as { response?: { data?: { message?: string; code?: string } } }
      )?.response;
      message.error(res?.data?.message ?? "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  const agregarOpcion = async () => {
    const label = nuevaOpcion.trim();
    if (!label || !code) return;
    try {
      const { body, config } = cuerpoDelValor(label, imagenNueva, colorNuevo);
      await axiosInstance.post(
        `${API_URL_PIM_ATTRIBUTES}/${encodeURIComponent(code)}/options`,
        body,
        config,
      );
      const muestra = colorNuevo ?? (imagenNueva ? URL.createObjectURL(imagenNueva) : null);
      if (muestra) setMuestrasCreadas((m) => ({ ...m, [label.toLowerCase()]: muestra }));
      setNuevaOpcion("");
      setImagenNueva(null);
      setColorNuevo(null);
      await cargar();
      // El normalizador puede haber reusado una existente: se recarga y se ve el resultado.
      message.success("Opción agregada");
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo agregar la opción",
      );
    }
  };

  const renombrarOpcion = async () => {
    if (!renombrando || !code) return;
    try {
      await axiosInstance.patch(
        `${API_URL_PIM_ATTRIBUTES}/${encodeURIComponent(code)}/options/${
          renombrando.optionId
        }`,
        { label: renombrando.label.trim() },
      );
      setRenombrando(null);
      await cargar();
      message.success("Opción renombrada");
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo renombrar",
      );
    }
  };

  const esDeSistema = !!impacto?.isStatic;
  // Un atributo de sistema guarda sus datos fuera de las tablas de valor, así que la API no
  // lo marca con valores cargados: se bloquea igual, porque su tipo no es del operador.
  const bloqueado = !!impacto?.frontendInputLocked || esDeSistema;

  return (
    // El panel lateral estándar de la app (`LateralModal`): mismo ancho, mismo
    // encabezado con la X a la derecha, y los botones abajo —Cancelar a la izquierda,
    // Guardar a la derecha con el check— como el resto de los paneles (xcons-docs#408).
    <LateralModal
      hideTrigger
      openDrawer={open}
      closeDrawer={onClose}
      title={esNuevo ? "Nuevo atributo" : `Atributo ${code}`}
    >
      <CuerpoConPie
        pie={
          <>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              iconPosition="end"
              loading={guardando}
              onClick={() => void guardar()}
            >
              Guardar
            </Button>
          </>
        }
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Flex vertical gap={16}>
            {/* El aviso de impacto, arriba de todo y antes de tocar nada. Un atributo de sistema
                no tiene conteo que mostrar, así que no lleva aviso (xcons-docs#408). */}
            {impacto && !impacto.isStatic && (
              <Alert
                type={impacto.products > 0 ? "warning" : "info"}
                showIcon
                message={`${impacto.products.toLocaleString(
                  "es-AR",
                )} productos usan este atributo`}
                // Lo que pidió el PM (B2.3): cuántos productos y cuántos valores muestra la
                // faceta que alimenta, nada más (xcons-docs#408).
                description={
                  impacto.feedsFacets && (
                    <Tag color="blue">
                      {impacto.facetValues === null
                        ? "Alimenta las facetas del catálogo"
                        : `Alimenta una faceta con ${impacto.facetValues.toLocaleString(
                            "es-AR",
                          )} ${impacto.facetValues === 1 ? "valor" : "valores"}`}
                    </Tag>
                  )
                }
              />
            )}

            <Form form={form} layout="vertical" disabled={guardando}>
              <Form.Item
                name="code"
                label="Código"
                rules={[
                  { required: true, message: "El código es obligatorio" },
                  {
                    pattern: /^[a-z][a-z0-9_]{1,58}$/,
                    message: "Minúsculas, números y _, empezando con una letra",
                  },
                ]}
                extra="Es la clave con la que los productos guardan el valor: no se puede cambiar después."
              >
                <Input disabled={!esNuevo} placeholder="vv_espesor" />
              </Form.Item>

              <Form.Item
                name="label"
                label="Etiqueta"
                rules={[
                  { required: true, message: "La etiqueta es obligatoria" },
                ]}
              >
                <Input placeholder="Espesor" />
              </Form.Item>

              <Form.Item
                name="frontendInput"
                label="Tipo"
                rules={[{ required: true, message: "El tipo es obligatorio" }]}
                extra={
                  esDeSistema
                    ? "Bloqueado: es un atributo de sistema."
                    : bloqueado
                    ? "Bloqueado: el atributo ya tiene valores cargados."
                    : "No se puede cambiar una vez que hay datos."
                }
              >
                <Select disabled={bloqueado} options={TIPOS} />
              </Form.Item>

              <Form.Item
                name="position"
                label="Orden"
                extra="Posición entre las facetas del catálogo: los números más bajos van primero."
              >
                <InputNumber min={0} precision={0} style={{ width: 160 }} placeholder="100" />
              </Form.Item>

              <Paragraph strong style={{ marginBottom: 8 }}>
                Config de catálogo
              </Paragraph>
              <Flex vertical>
                {CONFIG.map((c) => (
                  <Form.Item
                    key={c.key}
                    name={c.key}
                    valuePropName="checked"
                    style={{ marginBottom: 4 }}
                  >
                    <Checkbox>
                      {c.label}{" "}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        — {c.hint}
                      </Text>
                    </Checkbox>
                  </Form.Item>
                ))}
              </Flex>
            </Form>

            {/* El editor de opciones sólo existe para un atributo YA creado: las opciones se
              cuelgan de un attribute_id, así que un atributo nuevo se guarda primero. */}
            {!esNuevo && esOpcion(detalle?.frontendInput) && (
              <div>
                <Paragraph strong style={{ marginBottom: 8 }}>
                  Opciones ({detalle?.options.length ?? 0})
                </Paragraph>
                {/* La miniatura para la imagen y el nombre del valor, como en el Figma
                    (xcons-docs#490): la imagen sólo en los atributos que la llevan. */}
                <Flex gap={8} style={{ width: "100%", marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <CampoDeValor
                      value={nuevaOpcion}
                      placeholder="Nombre del valor"
                      onChange={setNuevaOpcion}
                      onPressEnter={() => void agregarOpcion()}
                      conImagen={
                        !!code && llevaImagen({ code, options: [], label: detalle?.label })
                      }
                      conColor={!!code && llevaColor({ code, label: detalle?.label })}
                      imagen={imagenNueva}
                      onImagen={setImagenNueva}
                      color={colorNuevo}
                      onColor={setColorNuevo}
                    />
                  </div>
                  <Button
                    icon={<PlusOutlined />}
                    disabled={!nuevaOpcion.trim()}
                    onClick={() => void agregarOpcion()}
                  >
                    Agregar
                  </Button>
                </Flex>
                <List
                  size="small"
                  bordered
                  style={{ maxHeight: 280, overflowY: "auto" }}
                  dataSource={detalle?.options ?? []}
                  renderItem={(o) =>
                    renombrando?.optionId === o.optionId ? (
                      <List.Item>
                        <Space.Compact style={{ width: "100%" }}>
                          <Input
                            autoFocus
                            value={renombrando.label}
                            onChange={(e) =>
                              setRenombrando({
                                optionId: o.optionId,
                                label: e.target.value,
                              })
                            }
                            onPressEnter={() => void renombrarOpcion()}
                          />
                          <Button
                            type="primary"
                            disabled={!renombrando.label.trim()}
                            onClick={() => void renombrarOpcion()}
                          >
                            Guardar
                          </Button>
                          <Button onClick={() => setRenombrando(null)}>
                            Cancelar
                          </Button>
                        </Space.Compact>
                      </List.Item>
                    ) : (
                      <List.Item
                        actions={[
                          <Button
                            key="edit"
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() =>
                              setRenombrando({
                                optionId: o.optionId,
                                label: o.label ?? "",
                              })
                            }
                          />,
                        ]}
                      >
                        <Flex align="center" gap={8}>
                          {muestraDe(o) && <Muestra swatch={muestraDe(o)!} />}
                          <Text>
                            {o.label ?? (
                              <Text type="secondary">(sin etiqueta)</Text>
                            )}
                          </Text>
                        </Flex>
                      </List.Item>
                    )
                  }
                />
              </div>
            )}

            {/* Sin historial en el panel (xcons-docs#490). */}
          </Flex>
        )}
      </CuerpoConPie>
    </LateralModal>
  );
};
